// KÖZÖS lista-eszköztár — a lista-oldalak (accounts / clients / contacts / tickets …) egységes fejléc-sávja:
// kereső + „Szűrők" popover (mindig összecsukott — Linear-minta) + darabszám-meta + akció-gomb.
//
// MIÉRT összecsukott szűrők: a korábbi per-oldal inline megoldás fix magasságú (`lg:h-14`) + `flex-wrap` volt,
// ami ~1024–1150px-en a szűrőket a 2. sorba tördelte és a sáv KILÓGOTT a fix 56px-ből, rá a tábla fejlécére.
// Itt a sáv `min-h-14` (NEM fix) → sosem csordul túl, és a szűrők egyetlen gomb mögé csukódnak → a sáv minden
// méretben tiszta, tetszőleges szűrő-számra skálázódik. Mobilon a kereső + „Szűrők (N)" gomb marad, nem zsúfolt.
//
// Popover-döntés (v1): minden méreten Popover (nincs külön mobil Sheet) — nincs `useMediaQuery`-hook a kódbázisban,
// és a Popover mobilon is jól használható; a bottom-sheet-variáns YAGNI, később bevezethető, ha egy oldal igényli.
import { FunnelIcon } from '@heroicons/react/24/outline';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { EntityMultiSelect, type MultiSelectGroup } from '@/components/shared/entity-multiselect';
import { MonthStepper } from '@/components/shared/month-stepper';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Divider } from '@/components/ui/divider';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

/**
 * Az „üres/mind" szűrő-érték alapértéke (per-szűrő felülírható az `allValue`-val).
 *
 * EXPORTÁLT, mert a hívó oldalon is kell: a lap a szűrő-állapotát ehhez méri („van-e szűkítés?"), és a
 * lekérdezésbe `undefined`-ként fordítja. Korábban ugyanez a literál a bevétel-előrejelzés eszköztárában is
 * ott ült külön néven — egy jelentés, egy hely.
 */
export const ALL_FILTER_VALUE = '__all__';
const DEFAULT_ALL = ALL_FILTER_VALUE;

export interface ToolbarFilter {
  /** Stabil kulcs (React-key + teszt-hook). */
  key: string;
  /** Emberi címke (a popover-mező fölött + a Select `aria-label`-je). */
  label: string;
  /** A kiválasztott érték (kontrollált). */
  value: string;
  onChange: (v: string) => void;
  /**
   * Az opciók. A `depth` FA-SZŰRŐN a szintet jelöli (0 = gyökér) — a behúzást a közös `SelectItem` rajzolja.
   * Enélkül a fa-szerű szűrő a szintet a NÉVBE írt írásjellel (`· `) jelölte, ami a szűrő-gomb feliratába
   * (a triggerbe) is beleragadt.
   */
  options: { value: string; label: string; depth?: number }[];
  /**
   * Az „mind" pszeudó-érték (alap: `__all__`). Ha `value === allValue`, a szűrő inaktív — MINDEN más érték aktív
   * szűrő, az is, amivel a lap indul (user-döntés, Andi #95: a lap alapból szűkített nézetét — pl. csak az aktív
   * munkatársak — a „Szűrők (N)" badge-nek jeleznie kell, és a „Szűrők törlése" a „mind"-re állít vissza).
   */
  allValue?: string;
  /** Az „mind" opció felirata (alap: `common.all`). */
  allLabel?: string;
  /**
   * NE kisbetűsítse az opció-feliratokat. A szűrő-legördülők alapból KISBETŰSEK (user-döntés: „a
   * »mind«, valamint az összes lehetőség minden select-ben egységesen kisbetűs legyen") — ez a kategória-,
   * státusz- és típus-facetekre igaz és szép.
   *
   * KIVÉTEL a TULAJDONNÉV: ügyfél-, fiók-, szolgáltatás-, csapat-, beosztás- és részleg-nevek (és minden más,
   * ami ADATBÓL jön, nem enumból) — azoknál a kisbetűsítés HIBA lenne („RS Booking" → „rs booking"). Az ilyen
   * faceteken kötelező a `preserveCase`.
   */
  preserveCase?: boolean;
  /** Letiltva — kaszkád-szűrőnél (a csomag csak szolgáltatás mellett, a fiók csak ügyfél mellett választható). */
  disabled?: boolean;
  /**
   * KERESHETŐ legördülő (a közös `EntityCombobox`): szövegközi, ékezet- és kisbetű-független keresés, és egy ×
   * gomb, ami a „mind" állapotra áll vissza. Hosszú tulajdonnév-listára való (ügyfél, fiók, szolgáltatás).
   *
   * MIÉRT KELL: a sima legördülő gépelésre csak az első, ÍGY KEZDŐDŐ elemre ugrik — a „Palace" nem hozta a
   * „Hévízi Palace Hotel Kft."-t, a választás pedig csak visszagörgetéssel volt törölhető (user-lelet).
   */
  searchable?: boolean;
}

/**
 * TÖBB-VÁLASZTÓS (multi) facet — a legacy `whereIn` megfelelője: üres kiválasztás = mind.
 *
 * A megjelenítés a közös `EntityMultiSelect` (legördülő + kereső + checkbox), nem kibontott checkbox-oszlop:
 * egy tucat alkategóriából álló lista háromszor annyi helyet vinne, mint a mellette álló Select-ek, és a
 * szűrő-panel nem lenne egyszerre átlátható (user-döntés).
 *
 * Aktív, ha van kiválasztás, és az nem az összes opció — akkor is, ha a lap így indul (pl. a bevétel-előrejelzés
 * alapból csak az aktív sorokat mutatja): a szűkítést a badge jelzi, a „Szűrők törlése" pedig ürít (user-döntés,
 * Andi #95). A teljes halmaz is „mind": ahol az üres kiválasztás mást jelent (a bevétel-előrejelzésnél az üres az
 * alap-nézetre esik vissza), ott a hívó az ürítést a teljes halmazra fordítja.
 */
export interface ToolbarMultiFilter {
  key: string;
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  options: { value: string; label: string }[];
  /** Lásd a `ToolbarFilter.preserveCase`-t: tulajdonnév-listán tilos kisbetűsíteni. */
  preserveCase?: boolean;
  /**
   * OPCIONÁLIS csoportosítás — a közös `EntityMultiSelect` csoport-fejlécei (a fejléc a csoport összes opcióját ki-/
   * bejelöli). Pl. a jogok a modul-csoportjuk szerint (Jogosultságok › Felhasználók).
   */
  groups?: MultiSelectGroup[];
  /** a csoportba nem tartozó opciók fejléce (csak `groups` mellett) */
  ungroupedLabel?: string;
}

/** Szabad-szöveges szűrő-mező (pl. „szereplő" az activity-logon). Inaktív, ha `value === ''`. */
export interface ToolbarTextFilter {
  key: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

/** Dátum-szűrő-mező (`type="date"`; egy dátum-tartományhoz kettő kell: -tól / -ig). Inaktív, ha `value === ''`. */
export interface ToolbarDateFilter {
  key: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}

/**
 * HÓNAP-szűrő (`YYYY-MM`, léptetővel) — a közös `MonthStepper`-t rendereli a popoverben.
 *
 * MIÉRT NEM „aktív szűrő": a hónap DIMENZIÓ, nem szűkítés — mindig van értéke (alap: a jelen hónap), tehát a
 * „Szűrők (N)" badge-be nem számol bele, és a „Szűrők törlése" sem nullázza (nincs mire).
 */
export interface ToolbarMonthFilter {
  key: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}

/**
 * SORREND-TARTÓ szűrő-elem: a három szűrő-fajta EGY listában, a hívó által megadott sorrendben.
 *
 * MIÉRT KELL: a `filters`/`textFilters`/`dateFilters` hármas FAJTA szerint rendez (előbb minden select, aztán
 * a szövegek, végül a dátumok) — a jegy-listán viszont a kért sorrend „hozzám rendelt · típus · dátum-tól ·
 * dátum-ig · support-csapat · státusz", tehát a dátumok KÖZÉPEN vannak. A `filterItems` ezt engedi; ha meg van
 * adva, a popover ebből renderel (a régi három prop változatlanul működik a többi lapon).
 */
export type ToolbarFilterItem =
  | ({ kind: 'select' } & ToolbarFilter)
  | ({ kind: 'multi' } & ToolbarMultiFilter)
  | ({ kind: 'text' } & ToolbarTextFilter)
  | ({ kind: 'date' } & ToolbarDateFilter)
  | ({ kind: 'month' } & ToolbarMonthFilter);

export interface ListToolbarProps {
  /**
   * Extra osztályok a SÁV külső elemére. Azért kell, mert a sáv vízszintes belső térköze (`px-4`) a BELSŐ
   * lista-oldalakhoz van hangolva: ott a sáv az `AppShell` szélig futó sticky-sávjában ül, tehát a `px-4`
   * hozza a lap-margót. A PARTNER-portálon a lap-tartalomnak MÁR van margója, ezért ott a `px-4` egy második
   * behúzást adott — a kereső beljebb kezdődött, mint a fülek és a kártyák (user-lelet).
   */
  className?: string;
  search: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    ariaLabel?: string;
  };
  /** Select-alapú (facet) szűrők. Ha mindhárom szűrő-tömb üres/hiányzik, a „Szűrők" gomb nem jelenik meg. */
  filters?: ToolbarFilter[];
  /** Szabad-szöveges szűrők (a Select-facetek mellett, ugyanabban a popoverben). */
  textFilters?: ToolbarTextFilter[];
  /** Dátum-szűrők (a popoverben, a facetek után). Egy tartomány = két mező (-tól/-ig). */
  dateFilters?: ToolbarDateFilter[];
  /** SORREND-TARTÓ szűrő-lista (fajta-keveréssel). Ha meg van adva, a fenti három propot FELÜLÍRJA. */
  filterItems?: ToolbarFilterItem[];
  /**
   * MINDIG LÁTHATÓ vezető elem a kereső ELŐTT (pl. a hónap-léptető a pénzügy-lap fülein).
   *
   * MIÉRT NEM a popoverben: a hónap a lap fő DIMENZIÓJA, nem szűkítés — a felhasználó léptetgeti, és ha
   * ehhez minden lépésnél ki kell nyitni egy popovert, az a lap legfőbb művelete lesz a legdrágább.
   */
  leading?: ReactNode;
  /** A „Szűrők" gomb UTÁN, a sávban maradó elem (pl. darabszám-jelvények). */
  trailing?: ReactNode;
  /** Jobb oldali meta (pl. darabszám) — CSAK ≥md. */
  meta?: ReactNode;
  /** Jobbra igazított akció(k) — pl. „+ Új" gomb. */
  actions?: ReactNode;
}

const allOf = (f: ToolbarFilter): string => f.allValue ?? DEFAULT_ALL;
/** A multi-facet akkor szűkít, ha van kiválasztás, és az nem az összes opció (a teljes halmaz = mind). */
const multiActive = (f: ToolbarMultiFilter): boolean =>
  f.values.length > 0 && !f.options.every((o) => f.values.includes(o.value));
/** A kiválasztott opció a kereshető legördülő címkéjéhez (URL-ből érkező értéknél is látszódjon a név). */
const selectedOption = (f: ToolbarFilter): { id: string; label: string } | null => {
  const o = f.options.find((x) => x.value === f.value);
  return o ? { id: o.value, label: o.label } : null;
};

/**
 * Reszponzív lista-eszköztár. A szűrők MINDIG a „Szűrők" gomb mögötti Popoverben élnek (nincs inline szűrő a
 * sávban), így a sáv sosem tördel/csordul. Az aktív szűrők száma badge-ként jelenik meg a gombon.
 */
export function ListToolbar({
  className,
  search,
  filters,
  textFilters,
  dateFilters,
  filterItems,
  leading,
  trailing,
  meta,
  actions,
}: ListToolbarProps): JSX.Element {
  const { t, i18n } = useTranslation();

  // A megjelenítés EGY sorrendezett listából megy. Ha a hívó `filterItems`-et ad, az a sorrend; egyébként a
  // régi (fajta szerinti) sorrendből építjük — így a meglévő lapok viselkedése bitre azonos marad.
  const items: ToolbarFilterItem[] = filterItems ?? [
    ...(filters ?? []).map((f) => ({ kind: 'select' as const, ...f })),
    ...(textFilters ?? []).map((f) => ({ kind: 'text' as const, ...f })),
    ...(dateFilters ?? []).map((f) => ({ kind: 'date' as const, ...f })),
  ];
  const selects = items.filter((f): f is { kind: 'select' } & ToolbarFilter => f.kind === 'select');
  const texts = items.filter((f): f is { kind: 'text' } & ToolbarTextFilter => f.kind === 'text');
  const dates = items.filter((f): f is { kind: 'date' } & ToolbarDateFilter => f.kind === 'date');
  const multis = items.filter(
    (f): f is { kind: 'multi' } & ToolbarMultiFilter => f.kind === 'multi',
  );
  const months = items.filter(
    (f): f is { kind: 'month' } & ToolbarMonthFilter => f.kind === 'month',
  );
  const hasFilters =
    selects.length > 0 ||
    multis.length > 0 ||
    texts.length > 0 ||
    dates.length > 0 ||
    months.length > 0;
  // A HÓNAP nem számít aktív szűrőnek (dimenzió, mindig van értéke) — lásd a `ToolbarMonthFilter` doc-ját.
  const activeCount =
    selects.filter((f) => f.value !== allOf(f)).length +
    multis.filter(multiActive).length +
    texts.filter((f) => f.value !== '').length +
    dates.filter((f) => f.value !== '').length;

  const clearAll = (): void => {
    selects.forEach((f) => {
      if (f.value !== allOf(f)) f.onChange(allOf(f));
    });
    multis.forEach((f) => {
      if (multiActive(f)) f.onChange([]);
    });
    texts.forEach((f) => {
      if (f.value !== '') f.onChange('');
    });
    dates.forEach((f) => {
      if (f.value !== '') f.onChange('');
    });
  };

  return (
    <div className={cn('flex min-h-14 flex-wrap items-center gap-2 px-4 py-2.5', className)}>
      {/* MOBILON a `leading` (ma: hónap-léptető) SAJÁT SORBAN áll, a kereső + „Szűrők" páros FÖLÖTT — 375 px-en
          hármasban egy sorba zsúfolva a kereső elfogyott volna. `sm`-től ugyanabban a sorban, elöl. A
          `w-full` a SORT foglalja el, a léptető maga `shrink-0`, tehát tartalom-szélességű marad. */}
      {leading !== undefined && (
        <div className="flex w-full sm:w-auto [&>*]:w-full sm:[&>*]:w-auto">{leading}</div>
      )}

      {/* Kereső: mobilon nyúlik (flex-1), desktopon fix szélesség. */}
      <Input
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
        placeholder={search.placeholder}
        aria-label={search.ariaLabel ?? search.placeholder}
        className="min-w-0 flex-1 lg:w-64 lg:flex-none"
      />

      {hasFilters && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="shrink-0"
              aria-label={t('common.filters')}
              data-active-count={activeCount}
            >
              <FunnelIcon />
              <span className="hidden sm:inline">{t('common.filters')}</span>
              {activeCount > 0 && (
                <Badge variant="brand" className="ml-0.5 h-4 min-w-4 justify-center px-1">
                  {activeCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          {/* A PANEL A GOMB JOBB SZÉLÉHEZ IGAZODIK (`align="end"`, user-döntés): a „Szűrők" gomb a sáv jobb
              végén ül, balra igazítva a panel átlógott a sáv közepére, és a széle sehol nem találkozott a
              gombéval. Jobbra igazítva a két él egy vonalban van — mobilon is befér (288 px panel, 375 px
              képernyő), mert a panel a gomb jobb élétől BALRA nyílik. */}
          <PopoverContent align="end" className="w-72 space-y-3">
            {items.map((f) => (
              <div key={`${f.kind}:${f.key}`} className="space-y-1.5">
                <Text variant="secondary" as="label" className="block">
                  {f.label}
                </Text>
                {f.kind === 'select' && f.searchable === true && (
                  <EntityCombobox
                    aria-label={f.label}
                    options={f.options.map((o) => ({ id: o.value, label: o.label }))}
                    value={f.value === allOf(f) ? null : f.value}
                    initialOption={selectedOption(f)}
                    onChange={(id) => f.onChange(id ?? allOf(f))}
                    placeholder={f.allLabel ?? t('common.all')}
                    lowercasePlaceholder
                    disabled={f.disabled === true}
                  />
                )}
                {f.kind === 'select' && f.searchable !== true && (
                  // A LEGÖRDÜLŐK KISBETŰSEK (user-döntés). Két szabály:
                  //   1. a „mind" opció MINDIG kisbetűs — a `preserveCase` faceten is (az a TULAJDONNEVEKRE
                  //      szól, a „mind" nem tulajdonnév),
                  //   2. az opció-feliratok kisbetűsek, KIVÉVE a `preserveCase` faceteken (ügyfél-, csapat-,
                  //      beosztás-nevek — „RS Booking" → „rs booking" hiba lenne).
                  // CSS-sel, nem i18n-átírással: ugyanaz a kulcs a badge-eken és a tábla-cellákban más
                  // tipográfiát kap, ott a nagybetű a helyes.
                  <Select value={f.value} onValueChange={f.onChange} disabled={f.disabled === true}>
                    <SelectTrigger
                      aria-label={f.label}
                      // A trigger a KIVÁLASZTOTT értéket mutatja: „mind" állapotban mindig kisbetűs.
                      className={
                        f.preserveCase !== true || f.value === allOf(f) ? 'lowercase' : undefined
                      }
                    >
                      <SelectValue placeholder={f.label} />
                    </SelectTrigger>
                    <SelectContent className={f.preserveCase === true ? undefined : 'lowercase'}>
                      <SelectItem value={allOf(f)} className="lowercase">
                        {f.allLabel ?? t('common.all')}
                      </SelectItem>
                      {f.options.map((o) => (
                        <SelectItem key={o.value} value={o.value} depth={o.depth ?? 0}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {/* MULTI-facet: a közös `EntityMultiSelect` (legördülő + kereső + checkbox). Üres = mind. */}
                {f.kind === 'multi' && (
                  <EntityMultiSelect
                    lowercaseOptions={f.preserveCase !== true}
                    // a „mind" (üres állapot) MINDIG kisbetűs — a tulajdonnév-faceten is (az nem tulajdonnév)
                    lowercasePlaceholder
                    options={f.options.map((o) => ({ id: o.value, name: o.label }))}
                    value={f.values}
                    onChange={f.onChange}
                    placeholder={t('common.all')}
                    countLabel={(count) => t('common.selectedCount', { count })}
                    groups={f.groups}
                    ungroupedLabel={f.ungroupedLabel}
                    aria-label={f.label}
                  />
                )}
                {f.kind === 'text' && (
                  <Input
                    value={f.value}
                    onChange={(e) => f.onChange(e.target.value)}
                    placeholder={f.placeholder ?? f.label}
                    aria-label={f.label}
                  />
                )}
                {/* Globális, design-rendszer szerinti dátumválasztó (a natív `type="date"` helyett) — a szerződés
                    változatlan `YYYY-MM-DD` string, így minden fogyasztó (activity-log/meetings) drop-in kapja. */}
                {f.kind === 'date' && (
                  <DatePicker
                    value={f.value}
                    onChange={f.onChange}
                    label={f.label}
                    language={i18n.language}
                  />
                )}
                {/* Hónap-léptető (`YYYY-MM`) — a közös komponens, teljes mező-szélességben. */}
                {f.kind === 'month' && (
                  <MonthStepper month={f.value} onMonthChange={f.onChange} className="w-full" />
                )}
              </div>
            ))}
            {activeCount > 0 && (
              <>
                {/* KÖZÖS, BEHÚZOTT elválasztó (nem érhet ki a popover széléig). */}
                <Divider spacing="sm" />
                <div className="flex items-center justify-between">
                  <Text variant="secondary" as="span">
                    {t('common.activeFilters', { count: activeCount })}
                  </Text>
                  <Button variant="ghost" size="sm" onClick={clearAll}>
                    {t('common.clearFilters')}
                  </Button>
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>
      )}

      {trailing}

      {/* Jobb oldali blokk: darabszám-meta (≥md) + akció(k). Az `ml-auto` a sávban jobbra tolja.
          MOBILON `display: contents` (MÉRT HIBA): a blokk akkor is FLEX-ELEM volt, ha MINDEN gyereke rejtve
          (a meta `md`-től, az akciók a FAB-ba költözve) — üresen is elvitte a szülő `gap`-jét, tehát a
          kereső + „Szűrők" sor 8 px-szel a jobb szél ELŐTT ért véget, miközben a fölötte álló hónap-léptető
          (`w-full`) a szélig futott. `contents` mellett a rejtett gyerekek nem hagynak nyomot, a mobilon is
          látható akciók (pl. tömeges kiállítás) pedig a sáv KÖZVETLEN elemeiként ülnek a helyükön. */}
      <div className="contents md:ml-auto md:flex md:items-center md:gap-3">
        {meta && (
          <div className="hidden items-center md:flex">
            <Text variant="secondary" as="span">
              {meta}
            </Text>
          </div>
        )}
        {actions}
      </div>
    </div>
  );
}
